/* eslint-disable unicorn/no-nested-ternary */

/* eslint-disable @typescript-eslint/no-magic-numbers */
import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as d3 from 'd3';
import { DateTime } from 'luxon';
import { Subscription, filter, merge, startWith } from 'rxjs';

import { fromSvgEvent$ } from '@shared/helpers/graph/svg-event.helper';
import { runOutsideAngularZone } from '@shared/operators/run-outside-angular-zone.operator';
import { D3ChartSvg, D3ChartSvgg, D3CursorEvent, D3XScale } from '@shared/types/common-graph.types';

import { ChartPointerPositionConfig, ChartXAxisConfig } from '../chart.view-model';
import { getClosestIndexToCursor, getTickValueText } from '../helpers/x-scale.helper';

export interface PointerPositionLabelRenderer {
  show: (text: string, x: number) => void;
  hide: () => void;
}

@Injectable()
export class PointerPositionService<Data extends object> {
  readonly #ngZone = inject(NgZone);
  readonly #destroyRef = inject(DestroyRef);

  private readonly verticalLineTopPartClass = 'vertical-line-top-part';
  private readonly verticalLineMiddlePartClass = 'vertical-line-middle-part';
  private readonly verticalLineBottomPartClass = 'vertical-line-bottom-part';
  private readonly verticalLineTopAndBottomPartRadius = 15;

  private pointerPositionVerticalLineEventListenerSubscription?: Subscription;

  private lastEvent?: D3CursorEvent;

  public resetListeners(): void {
    this.pointerPositionVerticalLineEventListenerSubscription?.unsubscribe();
  }

  public generatePointerPositionVerticalLine(
    data: Data[],
    svg: D3ChartSvg,
    linesContainer: D3ChartSvgg,
    width: number,
    height: number,
    xScale: D3XScale,
    xAxisConfig: ChartXAxisConfig<Data>,
    config?: ChartPointerPositionConfig,
    labelRenderer?: PointerPositionLabelRenderer,
    timeZone?: string
  ): void {
    const verticalLine = this.createVerticalLine(linesContainer, height, xScale);

    this.pointerPositionVerticalLineEventListenerSubscription?.unsubscribe();
    this.pointerPositionVerticalLineEventListenerSubscription = new Subscription();

    const desktopStartEvent$ = fromSvgEvent$(svg, 'mouseenter.verticalLine mousemove.verticalLine click.verticalLine');

    const mobileStartEvent$ = fromSvgEvent$(svg, 'touchstart.verticalLine touchmove.verticalLine');

    this.pointerPositionVerticalLineEventListenerSubscription.add(
      merge(desktopStartEvent$, mobileStartEvent$)
        .pipe(
          startWith(this.lastEvent),
          filter((e): e is D3CursorEvent => !!e),
          runOutsideAngularZone(this.#ngZone),
          takeUntilDestroyed(this.#destroyRef)
        )
        .subscribe((event) => {
          this.lastEvent = event;
          this.handleCursorEventForVerticalLine(
            event,
            data,
            linesContainer,
            width,
            verticalLine,
            xScale,
            xAxisConfig,
            config,
            labelRenderer,
            timeZone
          );
        })
    );

    const desktopEndEvent$ = fromSvgEvent$(svg, 'mouseleave.verticalLine');

    const mobileEndEvent$ = fromSvgEvent$(svg, 'touchend.verticalLine touchcancel.verticalLine');

    this.pointerPositionVerticalLineEventListenerSubscription.add(
      merge(desktopEndEvent$, mobileEndEvent$)
        .pipe(runOutsideAngularZone(this.#ngZone), takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => {
          this.lastEvent = undefined;
          this.hideVerticalLine(verticalLine);
          labelRenderer?.hide();
        })
    );
  }

  private createVerticalLine(linesContainer: D3ChartSvgg, height: number, xScale: D3XScale): D3ChartSvgg {
    const lineYOffset = 6;
    const xPoint = xScale(xScale.domain()[0]);

    const overflow = 0;

    return linesContainer
      .append('g')
      .style('pointer-events', 'none')
      .style('display', 'none')
      .style('visibility', 'hidden')
      .call((g) =>
        g
          .append('line')
          .attr('x1', xPoint - this.verticalLineTopAndBottomPartRadius)
          .attr('x2', xPoint + this.verticalLineTopAndBottomPartRadius)
          .attr('y1', -1 * overflow)
          .attr('y2', -1 * overflow)
          .attr('class', `${this.verticalLineTopPartClass} stroke-foreground-primary-100`)
          .attr('stroke-width', 2)
      )
      .call((g) =>
        g
          .append('line')
          .attr('x1', xPoint)
          .attr('x2', xPoint)
          .attr('y1', -1 * overflow)
          .attr('y2', height + lineYOffset + overflow)
          .attr('stroke-dasharray', '22, 6')
          .attr('class', `${this.verticalLineMiddlePartClass} stroke-foreground-primary-100`)
          .attr('stroke-width', 2)
      )
      .call((g) =>
        g
          .append('line')
          .attr('x1', xPoint - this.verticalLineTopAndBottomPartRadius)
          .attr('x2', xPoint + this.verticalLineTopAndBottomPartRadius)
          .attr('y1', height + lineYOffset + overflow)
          .attr('y2', height + lineYOffset + overflow)
          .attr('class', `${this.verticalLineBottomPartClass} stroke-foreground-primary-100`)
          .attr('stroke-width', 2)
      );
  }

  private handleCursorEventForVerticalLine(
    event: D3CursorEvent,
    data: Data[],
    linesContainer: D3ChartSvgg,
    chartWidth: number,
    verticalLine: D3ChartSvgg,
    xScale: D3XScale,
    xAxisConfig: ChartXAxisConfig<Data>,
    config?: ChartPointerPositionConfig,
    labelRenderer?: PointerPositionLabelRenderer,
    timeZone?: string
  ): void {
    let x = d3.pointer(event, linesContainer.node())[0];

    const xValue = data[getClosestIndexToCursor(event, data, xAxisConfig.dataKey, xScale, linesContainer)!][xAxisConfig.dataKey] as
      | DateTime
      | number;

    const xValueMillis = typeof xValue === 'number' ? xValue : xValue.toMillis();

    if (x < 0) {
      x = 0;
    }
    if (x > chartWidth) {
      x = chartWidth;
    }

    const text = xAxisConfig.hourMinutesTickFormat
      ? `${DateTime.fromMillis(xValueMillis, { zone: timeZone }).toFormat('h')}${DateTime.fromMillis(xValueMillis, { zone: timeZone })
          .toFormat('a')
          .toUpperCase()}`
      : config?.formatter
        ? config.formatter(xValueMillis, timeZone)
        : getTickValueText(
            xValueMillis,
            data.map((x) => {
              const mtu = x[xAxisConfig.dataKey] as DateTime | number;
              return typeof mtu === 'number' ? mtu : mtu.toMillis();
            }),
            xAxisConfig.verticalTexts ?? false,
            timeZone
          );

    verticalLine
      .select(`.${this.verticalLineTopPartClass}`)
      .attr('x1', x - this.verticalLineTopAndBottomPartRadius)
      .attr('x2', x + this.verticalLineTopAndBottomPartRadius);

    verticalLine.select(`.${this.verticalLineMiddlePartClass}`).attr('x1', x).attr('x2', x);

    verticalLine
      .select(`.${this.verticalLineBottomPartClass}`)
      .attr('x1', x - this.verticalLineTopAndBottomPartRadius)
      .attr('x2', x + this.verticalLineTopAndBottomPartRadius);

    verticalLine.style('display', null);
    verticalLine.style('visibility', null);
    labelRenderer?.show(text, x);
  }

  private hideVerticalLine(verticalLine: D3ChartSvgg): void {
    verticalLine.style('display', 'none');
    verticalLine.style('visibility', 'hidden');
  }
}
